import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { message } from 'antd'
import epsClient, { getUrl } from 'enrollment-platform/api/eps.client'
import {
  getPaymentAssignmentStatusLabel,
  PaymentHistoryEntry,
  PRIORITY_LABELS,
  Priority,
} from './Utils/types'
import { getStatusPillColors } from './payment-assignment.style'
import { modalFontFamily } from './PaymentDetailModal/PaymentDetailModal.styles'
import {
  StatusUpdateFormKey,
  StatusUpdateFormKeyLabels,
} from './CaseManagement/statusUpdate.type'
import { formatTimestampPST } from 'helpers/datetime'
import formatCurrency from 'components/IntlCurrencyInput/format'

const LogContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f6f8fa;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  font-family: ${modalFontFamily};
`

const LogTitle = styled.h3`
  font-family: ${modalFontFamily};
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e1e6ea;
  letter-spacing: -0.01em;
  line-height: 1.4;
`

const EntryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const EntryCard = styled.div<{ $highlight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${({ $highlight }) => ($highlight ? '#faad14' : '#e0e5ea')};
  border-radius: 6px;
  box-shadow: ${({ $highlight }) =>
    $highlight
      ? '0 0 0 1px rgba(250, 173, 20, 0.35)'
      : '0 1px 2px rgba(15, 36, 50, 0.05)'};
`

const EntryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e8ecf0;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  font-family: ${modalFontFamily};
  line-height: 1.4;
`

const UpdatedByLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`

const UpdatedByLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #8c9ba8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const PerformerName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.01em;
`

const PerformerRole = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #5a6b7d;
  background: #e8ecf0;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.02em;
`

const SecondaryText = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #8c9ba8;
  line-height: 1.3;
`

const ActionLabel = styled.span`
  font-family: ${modalFontFamily};
  font-size: 14px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.01em;
  line-height: 1.4;
`

const StatusPill = styled.span<{
  $backgroundColor: string
  $textColor: string
}>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-family: ${modalFontFamily};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  line-height: 1.3;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
`

const DetailsSection = styled.div`
  margin-top: 0;
`

const DetailsHeading = styled.div`
  font-family: ${modalFontFamily};
  font-size: 10px;
  font-weight: 600;
  color: #5a6b7d;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
  line-height: 1.3;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 4px 16px;
  align-items: baseline;
  font-family: ${modalFontFamily};
  font-size: 13px;
  line-height: 1.45;
`

const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 16px;
  align-items: baseline;
  font-family: ${modalFontFamily};
  font-size: 13px;
  line-height: 1.45;
  white-space: nowrap;
`

const DetailLabel = styled.span`
  font-family: ${modalFontFamily};
  font-weight: 500;
  color: #4a5f73;
  font-size: 12px;
  letter-spacing: 0.02em;
  line-height: 1.4;
`

const DetailValue = styled.span`
  font-family: ${modalFontFamily};
  color: #000;
  font-weight: 400;
  font-size: 13px;
  word-break: break-word;
  line-height: 1.45;
`

const MetadataDownloadLink = styled.button`
  font-family: ${modalFontFamily};
  padding: 0;
  border: none;
  background: none;
  color: #1677ff;
  cursor: pointer;
  text-decoration: underline;
  font-size: 13px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const NoteBlock = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: #f0f4f8;
  border-radius: 6px;
  font-family: ${modalFontFamily};
  font-size: 13px;
  color: #000;
  font-weight: 400;
  line-height: 1.5;
`

const NoteLabel = styled.span`
  font-family: ${modalFontFamily};
  font-weight: 600;
  font-size: 11px;
  color: #5a6b7d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 4px;
`

const EmptyState = styled.div`
  font-family: ${modalFontFamily};
  font-size: 13px;
  color: #5a6b7d;
  text-align: center;
  padding: 24px 16px;
  font-weight: 500;
  line-height: 1.5;
`

const MetadataBlock = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: #f8f9fb;
  border: 1px solid #e8ecf0;
  border-radius: 6px;
  font-family: ${modalFontFamily};
  font-size: 12px;
  line-height: 1.45;
`

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 4px 12px;
  align-items: baseline;
`

export interface PaymentActivityLogProps {
  activityLogs: PaymentHistoryEntry[]
}

/** Capitalize first letter of each word, lowercase rest of each word. Preserves multi-word names (e.g. "JOHN SMITH" → "John Smith"). */
function toTitleCase(s: string): string {
  if (!s) return s
  return s
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const PAYMENT_INFO_SNAPSHOT_LABELS: Record<string, string> = {
  carrierApplicationPrimaryPaymentMethod: 'Primary Payment Method',
  carrierApplicationBinderPaymentType: 'Binder Payment Type',
  carrierApplicationBinderPaymentAutopayEnabled: 'Autopay Enabled',
  carrierApplicationPrimaryACHAccountNumber: 'ACH Account Number',
  carrierApplicationPrimaryACHAccountType: 'ACH Account Type',
  carrierApplicationPrimaryACHAccountUse: 'ACH Account Use',
}

const PAYMENT_INFO_SNAPSHOT_ORDER = [
  'carrierApplicationPrimaryPaymentMethod',
  'carrierApplicationBinderPaymentType',
  'carrierApplicationBinderPaymentAutopayEnabled',
  'carrierApplicationPrimaryACHAccountNumber',
  'carrierApplicationPrimaryACHAccountType',
  'carrierApplicationPrimaryACHAccountUse',
]

function formatSnapshotValue(
  value: string | boolean | null | undefined
): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return value || '—'
}

const METADATA_DISPLAY_ORDER = new Map(
  [
    // Top-level flags
    StatusUpdateFormKey.PRIORITY,
    StatusUpdateFormKey.DID_MAKE_PAYMENT,
    // Payment Submitted (BenefitEventPaMetadata lines 24-31)
    StatusUpdateFormKey.ACTUAL_PREMIUM_PAID,
    StatusUpdateFormKey.ACTUAL_PAYMENT_DATE,
    StatusUpdateFormKey.CONFIRMATION_CODE,
    StatusUpdateFormKey.DID_SET_UP_AUTOPAY,
    StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD,
    StatusUpdateFormKey.AUTOPAY_PULL_DAY,
    StatusUpdateFormKey.CHECK_NUMBER,
    StatusUpdateFormKey.PAYMENT_SUBMITTED_TX_ID,
    // Payment Bot (lines 34-37)
    StatusUpdateFormKey.PAYMENT_BOT_TASK_ID,
    StatusUpdateFormKey.PAYMENT_BOT_TASK_TYPE,
    StatusUpdateFormKey.PG_BOSS_JOB_ID,
    StatusUpdateFormKey.PAYMENT_BOT_VOICE_RECORDING_URL,
    // Paid Escalation (lines 39-41)
    StatusUpdateFormKey.ESCALATE_REASON,
    StatusUpdateFormKey.ESCALATE_EXPLANATION,
    // Terminated EE (lines 44-47)
    StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE,
    StatusUpdateFormKey.TICKET,
    StatusUpdateFormKey.EE_CONTACT,
    StatusUpdateFormKey.HR_CONTACT,
    // Clear (lines 50-52)
    StatusUpdateFormKey.CLEARED_COST,
    StatusUpdateFormKey.CLEARED_EXPECTED_COST,
    StatusUpdateFormKey.CLEARED_STATUS,
    // QLE Migration
    StatusUpdateFormKey.MOVED_FROM_BENEFIT_ID,
    // Additional Note
    StatusUpdateFormKey.ADDITIONAL_NOTE,
  ].map((key, i) => [key, i] as const)
)

/**
 * Allowed metadata keys for the activity log Metadata block.
 * Only these fields (from BenefitEventPaMetadata) are shown; everything else
 * (internal dedup keys, clearing hashes, PHI-adjacent fields, etc.) is hidden.
 */
const ALLOWED_METADATA_KEYS = new Set<string>([
  // Top-level flags
  StatusUpdateFormKey.PRIORITY,
  StatusUpdateFormKey.DID_MAKE_PAYMENT,
  // Payment Submitted
  StatusUpdateFormKey.ACTUAL_PREMIUM_PAID,
  StatusUpdateFormKey.ACTUAL_PAYMENT_DATE,
  StatusUpdateFormKey.CONFIRMATION_CODE,
  StatusUpdateFormKey.DID_SET_UP_AUTOPAY,
  StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD,
  StatusUpdateFormKey.AUTOPAY_PULL_DAY,
  StatusUpdateFormKey.CHECK_NUMBER,
  StatusUpdateFormKey.PAYMENT_SUBMITTED_TX_ID,
  // Payment Bot
  StatusUpdateFormKey.PAYMENT_BOT_TASK_ID,
  StatusUpdateFormKey.PAYMENT_BOT_TASK_TYPE,
  StatusUpdateFormKey.PG_BOSS_JOB_ID,
  StatusUpdateFormKey.PAYMENT_BOT_VOICE_RECORDING_URL,
  // Paid Escalation
  StatusUpdateFormKey.ESCALATE_REASON,
  StatusUpdateFormKey.ESCALATE_EXPLANATION,
  // Terminated EE
  StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE,
  StatusUpdateFormKey.TICKET,
  StatusUpdateFormKey.EE_CONTACT,
  StatusUpdateFormKey.HR_CONTACT,
  // Clear
  StatusUpdateFormKey.CLEARED_COST,
  StatusUpdateFormKey.CLEARED_EXPECTED_COST,
  StatusUpdateFormKey.CLEARED_STATUS,
  // QLE Migration
  StatusUpdateFormKey.MOVED_FROM_BENEFIT_ID,
  // Additional Note
  StatusUpdateFormKey.ADDITIONAL_NOTE,
])

/**
 * PPS error.metaData keys always excluded regardless of allowlist.
 */
const HIDDEN_METADATA_KEYS = new Set<string>(['errorCode', 'fields'])

function sortMetadataEntries(
  entries: [string, unknown][]
): [string, unknown][] {
  const fallback = METADATA_DISPLAY_ORDER.size
  return [...entries].sort(
    ([a], [b]) =>
      (METADATA_DISPLAY_ORDER.get(a as StatusUpdateFormKey) ?? fallback) -
      (METADATA_DISPLAY_ORDER.get(b as StatusUpdateFormKey) ?? fallback)
  )
}

function formatMetadataLabel(key: string): string {
  const label =
    StatusUpdateFormKeyLabels[key as keyof typeof StatusUpdateFormKeyLabels]
  if (label !== undefined) return label

  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

const CURRENCY_METADATA_KEYS = new Set<string>([
  StatusUpdateFormKey.ACTUAL_PREMIUM_PAID,
  StatusUpdateFormKey.CLEARED_COST,
  StatusUpdateFormKey.CLEARED_EXPECTED_COST,
])

function formatMetadataCurrency(value: unknown): string {
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) return formatMetadataValue(value)
  return formatCurrency(num)
}

function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = dayjs(value)
      return d.isValid() ? d.format('MM-DD-YYYY') : value
    }
    return value || '—'
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

async function downloadPaymentBotVoiceRecording(path: string): Promise<void> {
  const { data } = await epsClient.get(path, { responseType: 'blob' })
  const filename = path.replace(/\/api\/v1\//g, '').replace(/\//g, '-')
  const blobUrl = URL.createObjectURL(data)
  try {
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

function PaymentBotVoiceRecordingDownloadButton({ path }: { path: string }) {
  const [loading, setLoading] = React.useState(false)
  const trimmed = path.trim()
  const fullUrl = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `${getUrl()}/${path}`

  const handleClick = async () => {
    if (!trimmed || loading) return
    setLoading(true)
    try {
      await downloadPaymentBotVoiceRecording(trimmed)
    } catch (err) {
      console.error(err)
      message.error('Download failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <MetadataDownloadLink
      type="button"
      title={fullUrl}
      disabled={loading}
      onClick={() => void handleClick()}
    >
      {loading ? 'Downloading…' : 'Download Voice Recording'}
    </MetadataDownloadLink>
  )
}

export default function PaymentActivityLog({
  activityLogs,
}: PaymentActivityLogProps) {
  // Sort by timestamp descending (newest first)
  const sortedHistory = [...activityLogs].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  return (
    <LogContainer>
      <LogTitle>Activity Log</LogTitle>
      {sortedHistory.length === 0 ? (
        <EmptyState>No activity found.</EmptyState>
      ) : (
        <EntryList>
          {sortedHistory.map((entry) => {
            const performerName = entry.actorName || entry.actor || 'System'
            const statusLabel =
              getPaymentAssignmentStatusLabel(entry.statusAfter) ||
              entry.statusAfter ||
              ''
            const statusColors = getStatusPillColors(entry.statusAfter)
            const description =
              entry.description ||
              (entry.statusBefore &&
              entry.statusAfter &&
              entry.statusBefore !== entry.statusAfter
                ? `Changed from "${entry.statusBefore}" to "${entry.statusAfter}"`
                : entry.reason || '')

            const assigneeDisplay =
              entry.assigneeName || entry.assigneeEmail || '-'
            const escalatorDisplay =
              entry.escalatorName || entry.escalatorEmail || '-'

            // Only show the explicitly allowed BenefitEventPaMetadata keys in
            // the defined order. Hidden-always keys (errorCode/fields) and any
            // internal/dedup fields not in ALLOWED_METADATA_KEYS are dropped.
            // Null/empty values are also suppressed.
            const metadataEntries = entry.metadata
              ? sortMetadataEntries(
                  Object.entries(entry.metadata).filter(
                    ([key, value]) =>
                      ALLOWED_METADATA_KEYS.has(key) &&
                      !HIDDEN_METADATA_KEYS.has(key) &&
                      value != null &&
                      value !== ''
                  )
                )
              : []

            const hasMovedFromBenefitId =
              entry.metadata != null &&
              'movedFromBenefitId' in entry.metadata &&
              entry.metadata.movedFromBenefitId != null &&
              entry.metadata.movedFromBenefitId !== ''

            return (
              <EntryCard key={entry.id} $highlight={hasMovedFromBenefitId}>
                <EntryHeader>
                  <HeaderLeft>
                    {entry.actionType !== 'Status Updated' ? (
                      <ActionLabel>{entry.actionType}</ActionLabel>
                    ) : null}
                    {statusLabel ? (
                      <StatusPill
                        $backgroundColor={statusColors.backgroundColor}
                        $textColor={statusColors.textColor}
                      >
                        {statusLabel}
                      </StatusPill>
                    ) : null}
                  </HeaderLeft>
                  <HeaderRight>
                    <UpdatedByLine>
                      <UpdatedByLabel>Updated by</UpdatedByLabel>
                      {entry.hasCreatedByUser && (
                        <PerformerName>
                          {toTitleCase(performerName)}
                        </PerformerName>
                      )}
                      {entry.performedByRole ? (
                        <PerformerRole>
                          {toTitleCase(entry.performedByRole)}
                        </PerformerRole>
                      ) : null}
                    </UpdatedByLine>
                    <SecondaryText>
                      {formatTimestampPST(entry.timestamp)}
                    </SecondaryText>
                  </HeaderRight>
                </EntryHeader>
                {entry.actionType === 'Payment Info Updated' ? (
                  <DetailsSection>
                    <DetailsHeading>Updated Fields</DetailsHeading>
                    {entry.snapshot &&
                    Object.keys(entry.snapshot).length > 0 ? (
                      <SnapshotGrid>
                        {PAYMENT_INFO_SNAPSHOT_ORDER.filter(
                          (key) => entry.snapshot && key in entry.snapshot
                        ).map((key) => (
                          <React.Fragment key={key}>
                            <DetailLabel>
                              {PAYMENT_INFO_SNAPSHOT_LABELS[key] ??
                                formatMetadataLabel(key)}
                            </DetailLabel>
                            <DetailValue>
                              {formatSnapshotValue(entry.snapshot?.[key])}
                            </DetailValue>
                          </React.Fragment>
                        ))}
                      </SnapshotGrid>
                    ) : (
                      <DetailValue>
                        Payment information was updated.
                      </DetailValue>
                    )}
                  </DetailsSection>
                ) : (
                  <DetailsSection>
                    <DetailsHeading>Details</DetailsHeading>
                    <DetailsGrid>
                      <DetailLabel>Assignee</DetailLabel>
                      <DetailValue>{toTitleCase(assigneeDisplay)}</DetailValue>
                      <DetailLabel>Escalator</DetailLabel>
                      <DetailValue>{toTitleCase(escalatorDisplay)}</DetailValue>
                    </DetailsGrid>
                  </DetailsSection>
                )}
                {entry.notes ? (
                  <NoteBlock>
                    <NoteLabel>Note</NoteLabel>
                    {entry.notes}
                  </NoteBlock>
                ) : null}
                {description ? (
                  <DetailsSection style={{ marginTop: 8 }}>
                    <DetailsGrid>
                      <DetailLabel>Summary</DetailLabel>
                      <DetailValue>{description}</DetailValue>
                    </DetailsGrid>
                  </DetailsSection>
                ) : null}
                {metadataEntries.length > 0 ? (
                  <MetadataBlock style={{ marginTop: 8 }}>
                    <DetailsHeading>Metadata</DetailsHeading>
                    <MetadataGrid>
                      {metadataEntries.map(([key, value]) => (
                        <React.Fragment key={key}>
                          <DetailLabel>{formatMetadataLabel(key)}</DetailLabel>
                          <DetailValue>
                            {key ===
                              StatusUpdateFormKey.PAYMENT_BOT_VOICE_RECORDING_URL &&
                            typeof value === 'string' &&
                            value.trim() ? (
                              <PaymentBotVoiceRecordingDownloadButton
                                path={value}
                              />
                            ) : key === StatusUpdateFormKey.PRIORITY &&
                              typeof value === 'number' &&
                              value in PRIORITY_LABELS ? (
                              PRIORITY_LABELS[value as Priority]
                            ) : CURRENCY_METADATA_KEYS.has(key) ? (
                              formatMetadataCurrency(value)
                            ) : (
                              formatMetadataValue(value)
                            )}
                          </DetailValue>
                        </React.Fragment>
                      ))}
                    </MetadataGrid>
                  </MetadataBlock>
                ) : null}
              </EntryCard>
            )
          })}
        </EntryList>
      )}
    </LogContainer>
  )
}
